import React from 'react';
import { Box, CircularProgress, Fab, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

interface UploadFabProps {
    uploadStatus: 'uploading' | 'success' | 'error' | null;
    handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
    selectedFile: File | null;
    uploadProgress: number;
    uploadError: string | null;
}

const UploadFab: React.FC<UploadFabProps> = ({ uploadStatus, handleFileSelect, selectedFile, uploadProgress, uploadError }) => {
    const getDesktopUploadIcon = () => {
        switch (uploadStatus) {
            case 'uploading':
                return <CircularProgress size={28} sx={{ color: 'white' }} />;
            case 'success':
                return <CheckCircleIcon sx={{ fontSize: 28 }} />;
            case 'error':
                return <ErrorIcon sx={{ fontSize: 28 }} />;
            default:
                return <AddIcon sx={{ fontSize: 28 }} />;
        }
    };

    const getDesktopFabColor = () => {
        switch (uploadStatus) {
            case 'success':
                return '#4caf50';
            case 'error':
                return '#f44336';
            case 'uploading':
                return '#2e7d32';
            default:
                return '#2e7d32';
        }
    };

    return (
        <>
            <Fab
                color="primary"
                size="large"
                disabled={uploadStatus === 'uploading'}
                onClick={() => document.getElementById('file-input-desktop')?.click()}
                sx={{
                    position: 'fixed',
                    bottom: '24px',
                    right: '24px',
                    width: '64px',
                    height: '64px',
                    backgroundColor: getDesktopFabColor(),
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                    zIndex: 1000,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        backgroundColor: uploadStatus === 'uploading' ? getDesktopFabColor() : '#1b5e20',
                        transform: uploadStatus === 'uploading' ? 'none' : 'scale(1.1)',
                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                    },
                    '&:active': {
                        transform: uploadStatus === 'uploading' ? 'none' : 'scale(0.95)',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#bdbdbd',
                    },
                }}
            >
                {getDesktopUploadIcon()}
            </Fab>
            <input
                id="file-input-desktop"
                type="file"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                accept="image/*,application/pdf,.doc,.docx,.txt"
                disabled={uploadStatus === 'uploading'}
            />
            {(selectedFile || uploadStatus) && (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {uploadStatus === 'error' && '✗ Upload Failed'}
                    </Typography>
            )}
        </>
    );
};

export default UploadFab;